/**
 * @file service-request.service.ts
 * @module app/modules/service-request/services/service-request.service
 * @description API calls for the service request portal (backend routes under /service-request). The
 * server decides access from the caller's role, so these calls send no user or role.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import APICallService from '../../../../api/apiCallService';
import { serviceRequest } from '../../../../api/apiEndPoints';
import {
  CreateServiceRequestPayload,
  ServiceRequestAccess,
  ServiceRequestCounts,
  ServiceRequestListParams,
  ServiceRequestListResponse,
  ServiceRequest,
} from '../types/service-request.types';

/**
 * APICallService's `isDelete` flag makes it return the error body ({ error }) instead of
 * showing its own toast, so the form can show the server's message inline.
 */
const RETURN_ERROR_BODY = true;
const LIST_LIMIT = 100;

type ApiCall = {
  endpoint: string;
  params?: unknown;
  path?: unknown;
  warehouse?: string;
};

/** Runs one call; turns the v1 error body, or no response, into a thrown Error. */
const call = async <T>({
  endpoint,
  params,
  path,
  warehouse,
}: ApiCall): Promise<T> => {
  let response: unknown;
  try {
    response = await new APICallService(
      endpoint,
      params,
      path,
      undefined,
      undefined,
      RETURN_ERROR_BODY,
      undefined,
      warehouse
    ).callAPI();
  } catch {
    throw new Error('Could not reach the server. Please try again.');
  }
  if (response && typeof response === 'object' && 'error' in response) {
    throw new Error(
      String((response as { error: unknown }).error || 'Request failed')
    );
  }
  if (!response) {
    throw new Error('Request failed. Please try again.');
  }
  return response as T;
};

/** Actions this user's role can request (with their inputs) and approve. */
export const getServiceRequestAccess = () =>
  call<ServiceRequestAccess>({ endpoint: serviceRequest.access });

/** Requests for one scope and tab, newest or queue order as the server sorts them. */
export const listServiceRequests = ({ scope, tab }: ServiceRequestListParams) =>
  call<ServiceRequestListResponse>({
    endpoint: serviceRequest.listRequests,
    params: { scope, tab, limit: LIST_LIMIT },
  });

/**
 * Badge counts. `decidedSince` is when this browser last showed the user's History tab;
 * the server counts my decisions after it as newDecisions.
 */
export const getServiceRequestCounts = (decidedSince?: string) =>
  call<ServiceRequestCounts>({
    endpoint: serviceRequest.counts,
    params: decidedSince ? { decidedSince } : {},
  });

/** Raises a request in the selected warehouse. */
export const createServiceRequest = (
  body: CreateServiceRequestPayload,
  warehouseId?: string
) =>
  call<ServiceRequest>({
    endpoint: serviceRequest.raiseRequest,
    params: body,
    warehouse: warehouseId,
  });

/** Approves a request; the server runs its action and returns it Completed or Failed. */
export const approveServiceRequest = (id: string) =>
  call<ServiceRequest>({
    endpoint: serviceRequest.requestStep,
    path: { id, step: 'approve' },
    params: {},
  });

/** Declines a request with the approver's comment. */
export const declineServiceRequest = (id: string, comment: string) =>
  call<ServiceRequest>({
    endpoint: serviceRequest.requestStep,
    path: { id, step: 'decline' },
    params: { comment },
  });

/** Withdraws the caller's own pending request with their reason. */
export const withdrawServiceRequest = (id: string, reason: string) =>
  call<ServiceRequest>({
    endpoint: serviceRequest.requestStep,
    path: { id, step: 'withdraw' },
    params: { reason },
  });
